// Tool consolidation's input (story 56): the displaceable-vendor checklist, in place of questions.
// Ported from v2's ToolsChecklist and wired to the store's `tools` / `customTools`. Checked total
// is the tool spend removed. Untouched = the default checklist (ZoomInfo), flagged Assumed.
//
// Re-render-safe draft state: spend and custom-tool text are `useDraftState` text drafts keyed on
// the store epoch + use case; every keystroke also pushes the parsed number to the store.

import { AUTOFILL_IGNORE_PROPS } from '../../../_shared/autofill';
import { useDraftState } from '../../../_shared/hooks';
import { iconUrl } from '../../data/icons';
import { DEFAULT_TOOLS16, TOOLS16 } from '../../data/tools';
import { moneyBig } from '../../engine/format';
import { toolRows } from '../../engine/use-case-engine';
import { numberToInput, parseNumberInput } from '../../flow/number-input';
import { NumberField } from './NumberField';
import { calculatorActions, useCalculatorStore } from '../../state/calculator-store';
import { Ph } from '../Icon';

interface ToolsDraft {
  /** Spend text by TOOLS16 id; absent = show the store value. */
  spend: Record<string, string>;
  custom: { id: string; name: string; spend: string }[];
}

const spendOf = (text: string): number | '' => parseNumberInput(text) ?? '';

export function UseCaseTools({ draftKey }: { draftKey: string }) {
  const tools = useCalculatorStore((s) => s.tools);
  const customTools = useCalculatorStore((s) => s.customTools);

  const seed: ToolsDraft = {
    spend: {},
    custom: (customTools ?? []).map((c, i) => ({ id: `seed-${i}`, name: c.name, spend: numberToInput(c.spend === '' ? null : c.spend) })),
  };
  const { draft, setDraft } = useDraftState<ToolsDraft>(seed, draftKey);
  const d = draft ?? seed;

  const untouched = tools == null && !(customTools ?? []).length;
  const effective = tools ?? DEFAULT_TOOLS16;
  const total = toolRows(tools ?? DEFAULT_TOOLS16, customTools).reduce((t, r) => t + r.spend, 0);

  const pushCustom = (rows: ToolsDraft['custom']) =>
    calculatorActions.setCustomTools(rows.map((r) => ({ name: r.name, spend: spendOf(r.spend) })));

  return (
    <div className="vc3-tools">
      <div className="vc3-tools-head">
        <span>Tools Clay displaces: tick what’s in the stack and set each contract’s annual spend.</span>
        {untouched ? (
          <span className="vc3-assumed">Assumed: default checklist</span>
        ) : (
          <button
            type="button"
            className="vc3-linkbtn"
            onClick={() => {
              calculatorActions.resetTools();
              setDraft({ spend: {}, custom: [] });
            }}
          >
            Reset to default
          </button>
        )}
      </div>

      {TOOLS16.map((t) => {
        const checked = t.id in effective;
        const text = d.spend[t.id] ?? numberToInput(checked ? Number(effective[t.id]) || 0 : null);
        return (
          <div key={t.id} className="vc3-tool-row" data-checked={checked}>
            <input
              type="checkbox"
              id={`vc3-tool-${t.id}`}
              checked={checked}
              onChange={() => {
                calculatorActions.toggleTool(t.id);
                const { [t.id]: _drop, ...rest } = d.spend;
                setDraft({ spend: rest });
              }}
            />
            <label htmlFor={`vc3-tool-${t.id}`} className="vc3-tool-name">
              <img src={iconUrl(t.icon)} alt="" />
              {t.name}
            </label>
            <div className="vc3-affix vc3-tool-spend">
              <span aria-hidden>$</span>
              <NumberField
                className="vc3-input"
                {...AUTOFILL_IGNORE_PROPS}
                aria-label={`${t.name} annual spend`}
                disabled={!checked}
                value={checked ? text : ''}
                placeholder={String(t.def)}
                onChange={(txt) => {
                  setDraft({ spend: { ...d.spend, [t.id]: txt } });
                  calculatorActions.setToolSpend(t.id, spendOf(txt));
                }}
              />
            </div>
            <span className="vc3-tool-unit">/yr</span>
          </div>
        );
      })}

      {d.custom.map((c, i) => (
        <div key={c.id} className="vc3-tool-row" data-checked>
          <span aria-hidden />
          <input
            className="vc3-input vc3-tool-custom-name"
            {...AUTOFILL_IGNORE_PROPS}
            aria-label="Tool name"
            placeholder="Tool name"
            value={c.name}
            onChange={(e) => {
              const rows = d.custom.map((r, j) => (j === i ? { ...r, name: e.target.value } : r));
              setDraft({ custom: rows });
              pushCustom(rows);
            }}
          />
          <div className="vc3-affix vc3-tool-spend">
            <span aria-hidden>$</span>
            <NumberField
              className="vc3-input"
              {...AUTOFILL_IGNORE_PROPS}
              aria-label="Custom tool annual spend"
              value={c.spend}
              onChange={(txt) => {
                const rows = d.custom.map((r, j) => (j === i ? { ...r, spend: txt } : r));
                setDraft({ custom: rows });
                pushCustom(rows);
              }}
            />
          </div>
          <button
            type="button"
            className="vc3-iconbtn"
            title="Remove tool"
            aria-label="Remove tool"
            onClick={() => {
              const rows = d.custom.filter((_, j) => j !== i);
              setDraft({ custom: rows });
              pushCustom(rows);
            }}
          >
            <Ph name="x" size={14} />
          </button>
        </div>
      ))}

      <div className="vc3-tools-foot">
        <button
          type="button"
          className="vc3-btn vc3-btn-ghost"
          onClick={() => {
            const rows = [...d.custom, { id: `new-${Date.now()}-${d.custom.length}`, name: '', spend: '' }];
            setDraft({ custom: rows });
            pushCustom(rows);
          }}
        >
          <Ph name="plus" size={14} /> Add a tool
        </button>
        <span className="vc3-tools-total">
          <span>Displaced contract spend</span>
          <strong>{moneyBig(total)}</strong>
        </span>
      </div>
    </div>
  );
}
